import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth, useCrossmint, useWallet } from "@crossmint/client-sdk-react-ui";
import { CROSSMINT_ENVIRONMENT } from "./crossmintDepositConfig";
import { getSwapSigner } from "./getSwapSigner";

const WalletBootstrapContext = createContext({ error: "" });

export const useWalletBootstrap = () => useContext(WalletBootstrapContext);

// Keep wallet loading outside the login page. The provider returns undefined
// when creation fails; its status must settle before a second attempt.
const ProductionWalletBootstrap = ({ children }) => {
  const { user, jwt, status: authStatus } = useAuth();
  const { experimental_customAuth: walletAuth } = useCrossmint();
  const { wallet, status: walletStatus, getOrCreateWallet } = useWallet();
  const [phase, setPhase] = useState("initial");
  const [error, setError] = useState("");
  const attempt = useRef({ userId: null, signer: false, plain: false });

  useEffect(() => {
    if (CROSSMINT_ENVIRONMENT !== "production") return;
    if (!user?.id || !jwt || authStatus !== "logged-in") {
      if (attempt.current.userId !== null) {
        attempt.current = { userId: null, signer: false, plain: false };
        setPhase("initial");
        setError("");
      }
      return;
    }
    if (attempt.current.userId !== user.id) {
      attempt.current = { userId: user.id, signer: false, plain: false };
      setError("");
      if (phase !== "initial") {
        setPhase("initial");
        return;
      }
    }
    if (wallet) {
      if (error) setError("");
      return;
    }
    // Crossmint Auth publishes its JWT before WalletProvider receives it.
    if (walletAuth?.jwt !== jwt || (!user?.twitter?.username && !walletAuth?.email)) return;

    const userId = user.id;
    if (phase === "initial" && !attempt.current.signer) {
      attempt.current.signer = true;
      const loadWithSigner = async () => {
        try {
          if (user?.twitter?.username) {
            const loaded = await getOrCreateWallet({ chain: "base", signer: { type: "passkey" } });
            if (attempt.current.userId === userId && !loaded) setError("Could not load your wallet. Refresh and try again.");
            return;
          }
          const signer = await getSwapSigner("base");
          if (attempt.current.userId !== userId) return;
          if (!signer) {
            setPhase("plain");
            return;
          }
          const loaded = await getOrCreateWallet({
            chain: "base",
            signer: { type: "email" },
            delegatedSigners: [{ signer: signer.locator }],
          });
          if (attempt.current.userId === userId && !loaded) setPhase("plain");
        } catch (cause) {
          if (attempt.current.userId === userId) {
            console.error("Could not load wallet with signer:", cause);
            setPhase("plain");
          }
        }
      };
      void loadWithSigner();
      return;
    }

    if (phase === "plain" && walletStatus !== "in-progress" && !attempt.current.plain) {
      attempt.current.plain = true;
      const loadExisting = async () => {
        try {
          const loaded = await getOrCreateWallet({ chain: "base", signer: { type: "email" } });
          if (attempt.current.userId === userId && !loaded) {
            setError("Could not load your wallet. Refresh and try again.");
          }
        } catch (cause) {
          if (attempt.current.userId === userId) {
            console.error("Could not load existing wallet:", cause);
            setError("Could not load your wallet. Refresh and try again.");
          }
        }
      };
      void loadExisting();
    }
  }, [user?.id, user?.twitter?.username, jwt, authStatus, walletAuth?.jwt, walletAuth?.email, wallet, walletStatus, getOrCreateWallet, phase, error]);

  return <WalletBootstrapContext.Provider value={{ error }}>{children}</WalletBootstrapContext.Provider>;
};

export default ProductionWalletBootstrap;
